import * as React from "react";
import {cx} from "emotion";

import "./sc-credit-cards.scss";

import {isApiTrue}          from "@Common/logic/is-api-true";
import {cs}                 from "@Common/react/chain-services";
import {consumeContext}     from "@Common/react/context";
import {fragments}          from "@Common/react/fragments";
import {Invoke}             from "@Common/react/invoke";
import {Load2}              from "@Common/react/load2";
import {scnv}               from "@Common/react/state-change-nested-value";
import {Static2}            from "@Common/react/static-2";
import {UseState}           from "@Common/react/use-state";
import {InformationBadge}   from "@Common/ui-components/heavy/common/design2/information-badge/information-badge";
import {ErrorHighlighter}   from "@Common/ui-components/light/error-highlighter";
import {scrollIntoView}     from "@Common/ui-components/light/scroll-into-view";
import {ReCaptchaInvisible} from "@Common/ui-components/recaptcha/recaptcha";
import {SliderCheckbox}     from "@Common/ui-components/slider-checkbox/slider-checkbox";
import {setNestedValue}     from "@Common/utils/arr-path";
import {chain}              from "@Common/utils/fs";

import {CardList}      from "@Xpresso/routes/common/heavy/credit-cards/card-list/card-list";
import {CardModal}     from "@Xpresso/routes/common/heavy/credit-cards/card-modal/card-modal";
import {toCountryCode} from "@Xpresso/routes/customize/steps/checkout/payments-section/credit-cards-on-file/to-country-code";

export const ScCreditCards = ({initEnrollment, agentId, required, next}) => cs(
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("userLevels"),

    ["disabled", ({company}, next) => next(!required && isApiTrue(company?.prefs?.hideCreditCardCollection))],

    ["state", (_, next) => UseState({
        getInitValue: () => chain(
            initEnrollment?.payment_options?.creditCard || null,
            (_) => ({
                current: _,
                saved  : _,
            }),
        ),
        next,
    })],

    ["recaptcha", ({userLevels}, next) => ReCaptchaInvisible({next, isInternalUser: userLevels?.internalUser})],

    ["cards", ({apis, company, disabled}, next) => Load2({
        _key: JSON.stringify({company: !!company, disabled}),
        fetch: () => disabled ? [] : apis.next.creditCard.getCreditCards({idAgent: agentId}),
        next,
    })],
    ["calSelected", ({state, cards, disabled}, next) => next(
        disabled ? (
            null
        ) : state.value.current?.token && cards.value?.some((c) => c.cardToken === state.value.current?.token) ? (
            state.value.current?.token
        ) : (
            cards.value?.find((c) => c.isDefaultCard)?.cardToken
        )
    )],

    ["calUseDefaultCard", ({cards, state, calSelected}, next) => next(
        !cards.value ? (
            true
        ) : !cards.value.length ? (
            false
        ) : state.value.current?.use_default != null ? (
            state.value.current.use_default
        ) :  !calSelected ? (
            true
        ) : (
            cards.value.find((c) => c.cardToken === calSelected).isDefaultCard
        )

    )],

    ["eh", (_, next) => ErrorHighlighter({next})],
    ["domRef", (_, next) => Static2({next})],

    ({cards, state, apis, calSelected, eh, domRef, calUseDefaultCard, disabled, recaptcha}) => next({
        fulfilled: !!calSelected,
        enrollmentEditor: disabled ? {
            valid: true,
            changed: state.value.saved?.token != null,
            prepareSave: (enrollment) => ({
                newValue: setNestedValue(
                    enrollment, ["payment_options", "creditCard"],
                    null,
                ),
            }),
        } : {
            loading: cards.loading,
            $name: "credit cards",
            valid: !required || !!calSelected,
            changed: (
                state.value.saved?.token !== calSelected ||
                !!state.value.saved?.use_default !== !!calUseDefaultCard ||
                false
            ),
            prepareSave: (enrollment) => ({
                newValue: setNestedValue(
                    enrollment, ["payment_options", "creditCard"],
                    !calSelected ? null : {
                        token: calSelected,
                        "use_default": !!calUseDefaultCard,
                    },
                ),
                onSaved: () => scnv(state, ["saved"], () => ({
                    token: calSelected,
                    "use_default": !!calUseDefaultCard,
                })),
            }),
            errorsHandling: {
                highlightErrors: () => {
                    eh.start();
                    scrollIntoView(domRef.get());
                },
            },
        },

        render: ({showErrors}) => cs(
            ({}, next) => disabled ? null : next(),
            ({}, next) => fragments(
                next(),
                !calUseDefaultCard && !state.value.current?.use_default && Invoke({
                    action: () => scnv(state, ["current", "use_default"], () => false),
                })
            ),
            ({}, next) => (
                <div {...{
                    className: "sc-credit-cards-3wh",

                    "xd-qa-type": "card",
                    "xd-qa-name": "Credit Cards",
                }}>

                    <div className="header">
                        <div {...{
                            className: "title",
                            "xd-qa-type": "label",
                            "xd-qa-name": "Credit Cards Title",
                        }}>
                            Credit Cards
                        </div>

                        <div
                            className="right"
                            onClick={() => {
                                scnv(state, ["current"], (_) => ({
                                    token: !calUseDefaultCard ? null : _?.token,
                                    use_default: !calUseDefaultCard,
                                }));
                            }}
                        >
                            <div {...{
                                className: "label",
                                "xd-qa-type": "label",
                                "xd-qa-name": "Use My Default Card",
                            }}>
                                Use My Default Card{" "}
                                {InformationBadge({
                                    getTooltip: () => (
                                        <div className="tooltip-35j">
                                            <div className="message">
                                                I would like to use my Default Credit Card for any balance due, after available Credits are applied, on all future orders.
                                            </div>
                                            <div className="tip">
                                                Tip: You can change your Default Credit Card in your Account Settings at any time.
                                            </div>
                                        </div>
                                    ),
                                    xdQaName: "Use My Default Card tooltip"
                                })}
                            </div>
                            {SliderCheckbox({
                                value: calUseDefaultCard,
                                xdQaName: "Use My Default Card",
                            })}
                        </div>
                    </div>

                    {required && (
                        <div className={cx("errors", eh.css, {show: showErrors})} ref={domRef.set}>
                            {!calSelected && "Please select a Credit Card to complete your enrollment"}
                        </div>
                    )}

                    <div className={cx("box", {disabled: calUseDefaultCard})}>
                        {next()}
                    </div>
                </div>
            ),

            ["cardModal", ({}, next) => CardModal({next})],
            ({cardModal}) => <>
                {CardList({
                    cards: !calUseDefaultCard ? cards.value : cards.value?.filter((c) => c.cardToken === calSelected),
                    selectedCards: !calSelected ? [] : [{cardToken: calSelected}],
                    multiple: false,
                    onEditNonceCard: () => {},
                    onEditSavedCard: async (card) => cardModal.show({
                        recaptchaToken: (await recaptcha.execute())?.token,
                        card: {
                            ...card,
                            country: toCountryCode(card.country),
                        },
                        saveToProfile: true,
                        forceSaveToProfile: true,
                        isDefaultCard: card.isDefaultCard,
                        mode: "edit",
                        onSave: async ({card, isDefaultCard, btTokenResult}) => {
                            await apis.next.creditCard.addUpdate({
                                ...card,
                                "braintreeNonce" : btTokenResult.nonce,
                                "makeDefault": isDefaultCard,
                            });

                            await cards.reload();
                            scnv(state, ["current", "token"], () => card.cardToken);
                        },
                    }),
                    onDeleteSavedCard: async (card) => {
                        scnv(state, ["current", "token"], (_) => _ === card.cardToken ? null : _);
                        cards.onChange(cards.value.filter((c) => c.cardToken !== card.cardToken));
                        await apis.next.creditCard.deleteCard(card.cardToken);
                        await cards.reload();
                    },
                    onDeleteNonceCard: () => {},
                    // getAssumedAmount: () => 1,
                    // onSaveCardAmount: () => {},
                    // onDeselectSavedCard: () => {
                    //     scnv(state, ["current"], () => null);
                    // },
                    onSelectSavedCard: (card) => {
                        // console.log("Select", card);
                        scnv(state, ["current", "token"], () => card.cardToken);
                    },
                })}

                {!calUseDefaultCard && (
                    <div className="controls">
                        <button
                            onClick={async () => {
                                cardModal.show({
                                    recaptchaToken: (await recaptcha.execute())?.token,
                                    card: {
                                        "country" : "US"
                                    },
                                    forceSaveToProfile: true,
                                    saveToProfile: true,
                                    isDefaultCard: false,
                                    mode: "add",
                                    onSave: async ({card, isDefaultCard, btTokenResult}) => {
                                        try {
                                            await apis.next.creditCard.addUpdate({
                                                ...card,
                                                "braintreeNonce" : btTokenResult.nonce,
                                                "makeDefault": isDefaultCard,
                                            }, agentId);

                                            const oldCards = cards.value;
                                            const newCards = await cards.reload();
                                            const newCard = newCards.find((c) => !oldCards.some((c1) => c1.cardToken === c.cardToken));

                                            scnv(state, ["current", "token"], () => isDefaultCard ? null : newCard.cardToken);
                                        } catch (e) {
                                            alert("Failed to add new card.\n" + e);
                                        }
                                    },
                                });
                            }}
                            xd-qa-type="button"
                            xd-qa-name="ADD NEW CARD"
                        >
                            ADD NEW CARD
                        </button>
                    </div>
                )}
            </>
        ),
    }),
);
